/**
 * src/pages/Users.js
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';

export default function Users() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', role: 'viewer' });
  const [formError, setFormError] = useState('');
  const [creating, setCreating] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [optimisticStates, setOptimisticStates] = useState({});

  async function fetchUsers() {
    setError('');
    setLoading(true);
    try {
      const res = await api.get('/portal/users');
      setUsers(res.data);
    } catch (err) {
      if (!err.response) {
        setError('Unable to reach the server. Check your connection and try again.');
      } else {
        setError(err.response.data?.message || 'Failed to load users.');
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchUsers(); }, []);

  async function handleCreate(e) {
    e.preventDefault();
    setFormError('');
    setError('');
    setSuccess('');
    setCreating(true);
    try {
      await api.post('/portal/users', formData);
      setFormData({ email: '', password: '', role: 'viewer' });
      setShowForm(false);
      await fetchUsers();
      setSuccess('User created.');
    } catch (err) {
      if (!err.response) {
        setFormError('Unable to reach the server. Check your connection and try again.');
      } else {
        setFormError(err.response.data?.message || 'Failed to create user.');
      }
    } finally {
      setCreating(false);
    }
  }

  async function handleToggleEnabled(e, user) {
    e.stopPropagation();
    if (user.id === currentUser.id || togglingId === user.id) return;
    setError('');
    const newValue = !user.enabled;
    setOptimisticStates(prev => ({ ...prev, [user.id]: newValue }));
    setTogglingId(user.id);
    try {
      await api.put(`/portal/users/${user.id}/enabled`, { enabled: newValue });
      await fetchUsers();
    } catch (err) {
      setOptimisticStates(prev => ({ ...prev, [user.id]: undefined }));
      if (!err.response) {
        setError('Unable to reach the server. Check your connection and try again.');
      } else {
        setError(err.response.data?.message || 'Failed to update user.');
      }
    } finally {
      setTogglingId(null);
      setOptimisticStates(prev => { const s = { ...prev }; delete s[user.id]; return s; });
    }
  }

  function handleRowClick(user) {
    navigate(`/users/${user.id}`);
  }

  if (loading) return (
    <div className="page">
      <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</div>
    </div>
  );

  // Load failed with no data — show only the error, not the misleading empty state
  if (error && users.length === 0) return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Users</h1>
          <p className="page-subtitle">Manage who has access to this portal.</p>
        </div>
      </div>
      <div className="alert alert-error">
        {error}
        <button className="alert-retry" onClick={fetchUsers}>Try again</button>
      </div>
    </div>
  );

  return (
    <div className="page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">Users</h1>
          <p className="page-subtitle">Manage who has access to this portal.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(prev => !prev)}>
          {showForm ? 'Cancel' : '+ Add User'}
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Create user form */}
      {showForm && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="card-header">
            <span className="card-title">New User</span>
          </div>
          <div className="card-body">
            {formError && <div className="alert alert-error">{formError}</div>}
            <form onSubmit={handleCreate}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input className="form-input" type="email" value={formData.email}
                    onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                    placeholder="user@company.com" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input className="form-input" type="password" value={formData.password}
                    onChange={e => setFormData(p => ({ ...p, password: e.target.value }))}
                    placeholder="Min. 12 chars, upper + lower + digit" required />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-select" value={formData.role}
                  onChange={e => setFormData(p => ({ ...p, role: e.target.value }))}
                  style={{ maxWidth: '320px' }}>
                  <option value="viewer">Viewer — can view config and history</option>
                  <option value="admin">Admin — can edit config and manage users</option>
                </select>
              </div>
              <button type="submit" className="btn btn-primary" disabled={creating}>
                {creating ? 'Creating…' : 'Create User'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Users table */}
      <div className="card">
        {users.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👤</div>
            <div className="empty-state-title">No users yet</div>
            <div className="empty-state-text">Add users to give your team access to the portal.</div>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created by</th>
                  <th>Created</th>
                  <th>Enabled</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const effectiveEnabled = optimisticStates[u.id] !== undefined
                    ? optimisticStates[u.id] : u.enabled;
                  const isToggling = togglingId === u.id;
                  const isSelf = u.id === currentUser.id;

                  return (
                    <tr key={u.id} onClick={() => handleRowClick(u)}
                      style={{ cursor: 'pointer' }} title="Click to view or edit">
                      <td>
                        {u.email}
                        {isSelf && (
                          <span style={{ marginLeft: '6px', fontSize: '11px', color: 'var(--text-tertiary)' }}>
                            (you)
                          </span>
                        )}
                      </td>
                      <td><span className="badge badge-neutral">{u.role}</span></td>
                      <td>
                        {effectiveEnabled
                          ? <span className="badge badge-success"><span className="status-dot" />Active</span>
                          : <span className="badge badge-danger"><span className="status-dot" />Disabled</span>}
                      </td>
                      <td>{u.created_by_email || '—'}</td>
                      <td className="td-mono">{new Date(u.created_at).toLocaleString()}</td>
                      <td onClick={e => e.stopPropagation()}>
                        {!isSelf ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {isToggling && (
                              <div style={{
                                width: '14px', height: '14px',
                                border: '2px solid var(--border)',
                                borderTopColor: 'var(--accent)',
                                borderRadius: '50%',
                                animation: 'spin 0.6s linear infinite'
                              }} />
                            )}
                            <label className="toggle">
                              <input type="checkbox" checked={effectiveEnabled}
                                onChange={e => handleToggleEnabled(e, u)}
                                disabled={isToggling} />
                              <div className="toggle-track" />
                              <div className="toggle-thumb" />
                            </label>
                          </div>
                        ) : (
                          <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}